import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {ComplexPolymerAttachmentStyle} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ComplexPolymerConnectionType,
  ComplexPolymerType,
  ConjugateGraphQL,
  ConnectionGraphQL,
  ConnectionSideGraphQL,
  NucleotideModificationType,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {isMoleculeSimplePolymer, isPolymerBackboneStrand} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {NO_ACCESS_CHIP_TEXT} from 'ui-library/chips/NoAccessChip';

export interface ChemicalStructureImageData {
  __typename: string;
  base64Svg?: string;
  message?: string;
}

export function getChemicalStructureImage(
  id: string,
  simplePolymers: Array<SimplePolymerGraphQL>
): ChemicalStructureImageData {
  const simplePolymerCandidates = simplePolymers.filter((simplePolymer) => simplePolymer.id === id);
  assert(
    simplePolymerCandidates.length === 1,
    'Expected to find exactly one matching simple polymer with ID'
  );
  assert(
    isMoleculeSimplePolymer(simplePolymerCandidates[0]),
    'Expected simple polymer to be a molecule simple polymer'
  );
  return simplePolymerCandidates[0].chemicalStructureImage;
}

export function getIconId(entityId: string, simplePolymers: Array<SimplePolymerGraphQL>): string {
  const simplePolymer = simplePolymers.find(
    (polymer) =>
      (!isInaccessible(polymer.entity) && polymer.entity.id === entityId) ||
      (isInaccessible(polymer.entity) && polymer.entity.invalidId === entityId)
  );
  assert(simplePolymer != null, 'Expected matching simple polymer for conjugate entity');

  if (isInaccessible(simplePolymer.entity)) {
    // Return generic custom entity icon if entity is inaccessible
    return 'square-list';
  }

  assert(simplePolymer.entity.__typename === 'SequenceNode', 'Expected RNA simple polymer');
  return simplePolymer.entity.iconId;
}

export interface ConjugateConnection {
  id: string;
  entityId: string | null;
  entityName: string;
  helmReference: string;
  residueIndex: number | null;
  rGroup: number | null;
  modificationType: NucleotideModificationType | null;
  linkedEntityResidueIndex?: number;
  linkedEntityHelmReference?: string;
  linkedEntityRGroup: number | null;
  linkedEntityModificationType: NucleotideModificationType | null;
  attachmentStyle: ComplexPolymerAttachmentStyle | null;
  // The molfile for this conjugate connection and all linked conjugate connections.
  attachmentMolfile: ChemicalStructureDrawerValue;
}

export interface ConjugateConnectionMap {
  [id: string]: ConjugateConnection;
}

export function entityIsNotArchived(
  entityId: string | null,
  simplePolymers: Array<SimplePolymerGraphQL>
): boolean {
  const filteredEntities = simplePolymers
    .map((simplePolymer) => simplePolymer.entity)
    .filter((entity) => !isInaccessible(entity) && entity.id === entityId);
  if (filteredEntities.length === 0) {
    return true;
  }
  assert(!isInaccessible(filteredEntities[0]), 'Expected entity to be accessible');
  return !isArchived(filteredEntities[0]);
}

export function connectionSideNotAttachedInMap(
  connectionSide: ConnectionSideGraphQL,
  conjugateConnectionMap: ConjugateConnectionMap
): boolean {
  return (
    !conjugateConnectionMap.hasOwnProperty(connectionSide.polymerHelmReference) ||
    conjugateConnectionMap[connectionSide.polymerHelmReference].linkedEntityHelmReference == null
  );
}

export function makeConjugateConnections(
  attachedConjugates: Array<ConjugateGraphQL>,
  simplePolymers: Array<SimplePolymerGraphQL>,
  connections: Array<ConnectionGraphQL>,
  complexPolymerType: ComplexPolymerType
): ConjugateConnectionMap {
  // Create a map from conjugate GQL id to information about its connection. First, iterate through all simple
  // polymers and add them to the map. Then, iterate through attached conjugates and flesh out connection
  // information.
  const conjugateConnectionMap: ConjugateConnectionMap = {};
  simplePolymers.map((simplePolymer) => {
    if (!isPolymerBackboneStrand(complexPolymerType, simplePolymer.helmReference)) {
      conjugateConnectionMap[simplePolymer.helmReference] = {
        id: simplePolymer.id,
        entityId: isInaccessible(simplePolymer.entity) ? null : simplePolymer.entity.id,
        entityName: isInaccessible(simplePolymer.entity) ? NO_ACCESS_CHIP_TEXT : simplePolymer.entity.name,
        helmReference: simplePolymer.helmReference,
        residueIndex: null,
        rGroup: null,
        modificationType: null,
        linkedEntityRGroup: null,
        linkedEntityModificationType: null,
        attachmentStyle: null,
        attachmentMolfile: null,
      };
    }
    return null;
  });

  attachedConjugates.map((attachedConjugate) => {
    assert(
      conjugateConnectionMap.hasOwnProperty(attachedConjugate.polymerHelmReference),
      'Expected conjugateConnectionMap to have attached molecule in map'
    );
    conjugateConnectionMap[attachedConjugate.polymerHelmReference] = {
      id: attachedConjugate.id,
      entityId: isInaccessible(attachedConjugate.entity) ? null : attachedConjugate.entity.id,
      entityName: isInaccessible(attachedConjugate.entity)
        ? NO_ACCESS_CHIP_TEXT
        : attachedConjugate.entity.name,
      helmReference: attachedConjugate.polymerHelmReference,
      residueIndex: attachedConjugate.connection.target.residueIndex,
      rGroup: attachedConjugate.connection.target.rGroup,
      modificationType: attachedConjugate.connection.target.modificationType,
      linkedEntityResidueIndex: attachedConjugate.connection.source.residueIndex!,
      linkedEntityHelmReference: attachedConjugate.connection.source.polymerHelmReference,
      linkedEntityRGroup: attachedConjugate.connection.source.rGroup,
      linkedEntityModificationType: attachedConjugate.connection.source.modificationType,
      attachmentStyle: attachedConjugate.connection.attachmentStyle,
      attachmentMolfile: null,
    };
    attachedConjugate.linkedMolecules.map((linkedMolecule) => {
      assert(
        conjugateConnectionMap.hasOwnProperty(linkedMolecule.polymerHelmReference),
        'Expected conjugateConnectionMap to have linked molecule in map'
      );
      conjugateConnectionMap[linkedMolecule.polymerHelmReference] = {
        id: linkedMolecule.id,
        entityId: isInaccessible(linkedMolecule.entity) ? null : linkedMolecule.entity.id,
        entityName: isInaccessible(linkedMolecule.entity) ? NO_ACCESS_CHIP_TEXT : linkedMolecule.entity.name,
        helmReference: linkedMolecule.polymerHelmReference,
        residueIndex: linkedMolecule.connection.target.residueIndex,
        rGroup: linkedMolecule.connection.target.rGroup,
        modificationType: linkedMolecule.connection.target.modificationType,
        linkedEntityResidueIndex: linkedMolecule.connection.source.residueIndex ?? undefined,
        linkedEntityHelmReference: linkedMolecule.connection.source.referencedPolymer.helmReference,
        linkedEntityRGroup: linkedMolecule.connection.source.rGroup,
        linkedEntityModificationType: linkedMolecule.connection.source.modificationType,
        attachmentStyle: linkedMolecule.connection.attachmentStyle,
        attachmentMolfile: null,
      };
      return null;
    });
    return null;
  });

  for (const connection of connections) {
    if (connection.connectionType !== ComplexPolymerConnectionType.COVALENT_BOND) {
      continue;
    }
    if (
      connectionSideNotAttachedInMap(connection.source, conjugateConnectionMap) &&
      connectionSideNotAttachedInMap(connection.target, conjugateConnectionMap)
    ) {
      // We expect here that only conjugate-conjugate connections will not be represented in
      // attachedConjugates.
      assert(
        !isPolymerBackboneStrand(complexPolymerType, connection.source.polymerHelmReference) &&
          !isPolymerBackboneStrand(complexPolymerType, connection.target.polymerHelmReference),
        'Expected connection to be between conjugates'
      );
      assert(
        conjugateConnectionMap.hasOwnProperty(connection.target.polymerHelmReference),
        'Expected target connection side to be in conjugate connection map'
      );
      // Arbitrarily choose the target side of the connection to be the "owning" side of the connection.
      const existingConjugateConnection = conjugateConnectionMap[connection.target.polymerHelmReference];
      conjugateConnectionMap[connection.target.polymerHelmReference] = {
        id: existingConjugateConnection.id,
        entityId: existingConjugateConnection.entityId,
        entityName: existingConjugateConnection.entityName,
        helmReference: connection.target.polymerHelmReference,
        residueIndex: connection.target.residueIndex,
        rGroup: connection.target.rGroup,
        modificationType: connection.target.modificationType,
        linkedEntityResidueIndex: connection.source.residueIndex ?? undefined,
        linkedEntityHelmReference: connection.source.polymerHelmReference,
        linkedEntityRGroup: connection.source.rGroup,
        linkedEntityModificationType: connection.source.modificationType,
        attachmentStyle: connection.attachmentStyle,
        attachmentMolfile: null,
      };
    }
  }

  return conjugateConnectionMap;
}
